import * as React from 'react';
import { cn } from '@/lib/utils';
import { premiumTheme } from './tokens';

/**
 * InfoRow \u2014 Key/value pair for detail panels (Patient360, entity summaries).
 *
 * Layout: vertical label (10px uppercase) + value (12px medium).
 * Use `layout="horizontal"` for compact side-by-side display in dense panels.
 */

export interface InfoRowProps extends React.HTMLAttributes<HTMLDivElement> {
  label: React.ReactNode;
  value?: React.ReactNode;
  /** Fallback when value is empty */
  empty?: React.ReactNode;
  layout?: 'stacked' | 'horizontal';
  /** Truncate long values */
  truncate?: boolean;
  /** Left-side icon or badge */
  adornment?: React.ReactNode;
}

export const InfoRow = React.forwardRef<HTMLDivElement, InfoRowProps>(
  (
    {
      label,
      value,
      empty = '\u2014',
      layout = 'stacked',
      truncate = false,
      adornment,
      className,
      ...props
    },
    ref,
  ) => {
    const isEmpty = value === null || value === undefined || value === '';
    const displayValue = isEmpty ? empty : value;

    if (layout === 'horizontal') {
      return (
        <div
          ref={ref}
          className={cn(
            'flex items-center justify-between gap-3 py-1.5',
            className,
          )}
          {...props}
        >
          <span className={cn(premiumTheme.typography.label, 'flex-shrink-0')}>
            {label}
          </span>
          <span
            className={cn(
              premiumTheme.typography.value,
              'text-right min-w-0',
              truncate && 'truncate',
            )}
          >
            {displayValue}
          </span>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={cn('flex flex-col gap-0.5 py-1', className)}
        {...props}
      >
        <span className={premiumTheme.typography.label}>{label}</span>
        <div className="flex items-center gap-1.5 min-w-0">
          {adornment && <span className="flex-shrink-0">{adornment}</span>}
          <span
            className={cn(
              premiumTheme.typography.value,
              'min-w-0',
              truncate && 'truncate',
            )}
          >
            {displayValue}
          </span>
        </div>
      </div>
    );
  },
);
InfoRow.displayName = 'InfoRow';

/**
 * InfoRowGroup \u2014 Column layout helper for stacking multiple InfoRows with
 * consistent vertical rhythm.
 */
export const InfoRowGroup = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { columns?: 1 | 2 | 3 }
>(({ className, columns = 1, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'grid gap-x-4 gap-y-1',
      columns === 1 && 'grid-cols-1',
      columns === 2 && 'grid-cols-1 sm:grid-cols-2',
      columns === 3 && 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
      className,
    )}
    {...props}
  />
));
InfoRowGroup.displayName = 'InfoRowGroup';
